import { Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import "./i18n/config";
import BaseLayout from "./components/Layout/BaseLayout";
import LoginLayout from "./components/Layout/LoginLayout";
import HomeLayout from "./components/Layout/HomeLayout";
import BookingLayout from "./components/Layout/BookingLayout";
import { CartProvider } from "./context/CartContext";
import Login from "./pages/auth/Login";
import Dashboard from "./pages/Dashboard";
import AllBookings from "./pages/Bookings/AllBookings";
import LocationSwap from "./pages/LocationSwap/LocationSwap";
import BookingType from "./pages/Booking/BookingType";
import SelectSlots from "./pages/Booking/SelectSlots";
import BookingSuccess from "./pages/Booking/BookingSuccess";
import Shop from "./pages/Shop/Shop";
import ProductDetail from "./pages/Shop/ProductDetail";
import OrderSuccess from "./pages/Shop/OrderSuccess";

// Lazy load Checkout to prevent Stripe from loading on app initialization
const Checkout = lazy(() => import("./pages/Shop/Checkout"));
import OpenGate from "./pages/AccessControl/OpenGate";
import LocationVerification from "./pages/AccessControl/LocationVerification";
import MyGame from "./pages/MyGame/MyGame";
import Chat from "./pages/Chat/Chat";
import ProfileNavigation from "./pages/Profile/ProfileNavigation";
import PersonalInformation from "./pages/Profile/PersonalInformation";
import EditProfile from "./pages/Profile/EditProfile";
import MyOrders from "./pages/Orders/MyOrders";
import OrderDetails from "./pages/Orders/OrderDetails";
import Membership from "./pages/Membership/Membership";
import UpdatePaymentMethod from "./pages/Membership/UpdatePaymentMethod";
import NotificationPreference from "./pages/NotificationPreference/NotificationPreference";
import InputTest from "./pages/test/InputTest";
import ButtonTest from "./pages/test/ButtonTest";
import FooterNavDemo from "./pages/test/FooterNavDemo";
import { AuthGuard } from "./components/guards/AuthGuard";
import { MemberAccessGuard } from "./components/guards/MemberAccessGuard";
import WaiverSigningPage from "@/pages/waiver/WaiverSigningPage";
import { PublicRoutePath } from "./config/routes";
import MicrositeLayout from "./pages/microsite/MicrositeLayout";
import MicrositeErrorPage from "./pages/microsite/MicrositeErrorPage";
import MicrositeAccessPage from "./pages/microsite/MicrositeAccessPage";
import MicrositeHomePage from "./pages/microsite/MicrositeHomePage";
import MicrositeReportPage from "./pages/microsite/MicrositeReportPage";
import MicrositePlanPage from "./pages/microsite/MicrositePlanPage";
import MicrositeBenefitsPage from "./pages/microsite/MicrositeBenefitsPage";
import SurveyLayout from "./pages/survey/SurveyLayout";
import SurveyErrorPage from "./pages/survey/SurveyErrorPage";
import SurveyFormPage from "./pages/survey/SurveyFormPage";
import GuestWaiverLayout from "./pages/guest-waiver/GuestWaiverLayout";
import GuestWaiverPage from "./pages/guest-waiver/GuestWaiverPage";
import { ToastProvider } from "./context/ToastContext";
import NativeCalendarResultBridge from "./components/NativeCalendarResultBridge";
import { BookingLocationScope } from "@/v2/components/BookingLocationScope";
import { StatusBarScope } from "@/v2/components/StatusBarScope";
import { VersionSyncProvider } from "./context/VersionSyncContext";
import UpdateRequiredModal from "./components/UpdateRequiredModal";
import { ThemeProvider } from "./context/ThemeContext";
import { ChatWidgetProvider } from "./context/ChatWidgetContext";
import ChatWidget from "./components/ui/organisms/ChatWidget";
import { useEffect, useState } from "react";
import { SStateSelector } from "./components/ui/molecules/SStateSelector";
import { SelectStateDrawer } from "./components/ui/organisms/SelectStateDrawer";
import { useAuth } from '@/hooks/useAuth';
import { UiBlock, UiRoute, UiV2Route, V2InstallPrompt, V2Welcome, V2Login, V2Dashboard, V2BookingType, V2SelectSlots, V2AllBookings, V2BookingSuccess, V2SessionDetails, V2MyGame, V2MyGameReport, V2MyGameNotesHistory, V2NoteThreadScreen, V2TopicsScreen, V2NewTopicScreen, V2ThreadScreen, V2MyGamePlan, V2Shop, V2ProductDetail, V2Cart, V2OrderSuccess, V2MyOrders, V2OrderDetails, V2Profile, V2Membership, V2MyPlan, V2PersonalInformation, V2UpdatePaymentMethod, V2WaiverSigning, V2Community, V2CommunityMember, V2CommunitySettings, V2CommunityConnect, V2ShareTeeTime, V2TeeTimeDetail, V2TeeBuddies, V2CommunityScan, V2Notifications, V2Account, PublicV2Route, V2ShareReport } from "@/v2/routes";
import { PostHogProvider } from 'posthog-js/react';
import { POSTHOG_KEY, POSTHOG_OPTIONS } from '@/config/analytics.config';
import { appPath, isDemoMode } from '@/demo/demoMode';

// Logout handler component — triggered by navigation to /logout (e.g. native deep link)
function LogoutHandler() {
  const { logout } = useAuth();

  useEffect(() => {
    // Hard redirect (not navigate()) so no stale in-memory/router state — e.g. a
    // swapped booking location — survives into the next member's session.
    logout().finally(() => { window.location.href = appPath('/login'); });
  }, []);

  return null;
}

// Location page wrapper with state selector
function LocationPage() {
  const [selectedState, setSelectedState] = useState('Utah');
  const [isStateDrawerOpen, setIsStateDrawerOpen] = useState(false);

  return (
    <>
      <BookingLayout
        headerRightSlot={
          <SStateSelector
            state={selectedState}
            onClick={() => setIsStateDrawerOpen(true)}
          />
        }
        showHeader={false}
        showFooter={false}
      >
        <LocationSwap />
      </BookingLayout>
      <SelectStateDrawer
        isOpen={isStateDrawerOpen}
        onClose={() => setIsStateDrawerOpen(false)}
        currentState={selectedState}
        onStateSelect={(state) => setSelectedState(state)}
      />
    </>
  );
}


function App() {
  const content = (
    <ThemeProvider>
    <CartProvider>
      <div className="app-root">
        <Routes>
        {/* Public routes with LoginLayout */}
        <Route element={<LoginLayout />}>
          <Route
            path={PublicRoutePath.root}
            element={
              <AuthGuard requireAuth={false}>
                {/* v2 lands an unauthenticated visitor on the video
                    Welcome screen; v1 has no such screen and goes
                    straight to the form, as it always has. */}
                <UiRoute
                  v1={<Navigate to={PublicRoutePath.login} replace />}
                  v2={<Navigate to={PublicRoutePath.welcome} replace />}
                />
              </AuthGuard>
            }
          />
          <Route
            path={PublicRoutePath.welcome}
            element={
              <AuthGuard requireAuth={false}>
                {/* v2-only. A v1 session that reaches /welcome — an
                    old deep link, a shared URL — must not 404, so it
                    resolves to the screen v1 does have. */}
                <UiRoute
                  v1={<Navigate to={PublicRoutePath.login} replace />}
                  v2={<V2Welcome />}
                />
              </AuthGuard>
            }
          />
          <Route
            path={PublicRoutePath.login}
            element={
              <AuthGuard requireAuth={false}>
                <UiRoute v1={<Login />} v2={<V2Login />} />
              </AuthGuard>
            }
          />
        </Route>

        {/* Protected routes with HomeLayout */}
        <Route element={<HomeLayout />}>
          <Route 
            path={PublicRoutePath.home} 
            element={
              <AuthGuard requireAuth={true}>
                <UiRoute v1={<Dashboard />} v2={<V2Dashboard />} />
              </AuthGuard>
            } 
          />
        </Route>

        {/* Profile routes */}
        <Route 
          path="/profile"
          element={
            <AuthGuard requireAuth={true}>
              <UiRoute v1={<ProfileNavigation />} v2={<V2Profile />} />
            </AuthGuard>
          }
        />
        <Route 
          path="/profile/personal-information"
          element={
            <AuthGuard requireAuth={true}>
              <UiRoute v1={<PersonalInformation />} v2={<V2PersonalInformation />} />
            </AuthGuard>
          }
        />
        <Route 
          path="/profile/personal-information/edit" 
          element={
            <AuthGuard requireAuth={true}>
              <EditProfile />
            </AuthGuard>
          } 
        />

        {/* Orders routes */}
        <Route 
          path="/profile/orders" 
          element={
            <AuthGuard requireAuth={true}>
              <UiRoute v1={<MyOrders />} v2={<V2MyOrders />} />
            </AuthGuard>
          } 
        />
        <Route 
          path="/profile/orders/:id" 
          element={
            <AuthGuard requireAuth={true}>
              <UiRoute v1={<OrderDetails />} v2={<V2OrderDetails />} />
            </AuthGuard>
          } 
        />
        
        {/* Account — membership billing facts, orders, waivers,
            notification preferences and delete account. v2 only;
            v1 keeps those as separate screens off Profile. */}
        <Route
          path="/profile/account"
          element={
            <AuthGuard requireAuth={true}>
              <UiV2Route>
                <V2Account />
              </UiV2Route>
            </AuthGuard>
          }
        />

        {/* Membership route */}
        <Route
          path="/profile/membership"
          element={
            <AuthGuard requireAuth={true}>
              <UiRoute v1={<Membership />} v2={<V2Membership />} />
            </AuthGuard>
          }
        />

        {/* My Plan — what the membership includes and the rules it
            books under. v2 only; reached from the plan card's View
            more, which replaced the Membership row in the profile
            navigation. */}
        <Route
          path="/profile/my-plan"
          element={
            <AuthGuard requireAuth={true}>
              <UiV2Route>
                <V2MyPlan />
              </UiV2Route>
            </AuthGuard>
          }
        />

        {/* Payment method update — reachable while membership is past_due */}
        <Route
          path="/membership/update-payment"
          element={
            <AuthGuard requireAuth={true}>
              <UiRoute
                v1={<UpdatePaymentMethod />}
                v2={<V2UpdatePaymentMethod />}
              />
            </AuthGuard>
          }
        />

        {/* Notification Preference route */}
        <Route 
          path="/profile/notification-preference" 
          element={
            <AuthGuard requireAuth={true}>
              <UiRoute
                v1={<NotificationPreference />}
                v2={<Navigate to="/profile/account" replace />}
              />
            </AuthGuard>
          } 
        />
        
        {/* Protected routes with BaseLayout and title */}
        <Route
          path="/my-game"
          element={
            <AuthGuard requireAuth={true}>
              <UiRoute v1={<MyGame />} v2={<V2MyGame />} />
            </AuthGuard>
          }
        />

        {/* The in-app full assessment report and coaching-notes history.
            v2 only — there is no v1 counterpart, so these render through
            UiV2Route rather than UiRoute, following /profile/my-plan's
            pattern. Reachable only via links from within My Game itself
            (D-15): no TabBar entry, no bell entry. */}
        <Route
          path="/my-game/report"
          element={
            <AuthGuard requireAuth={true}>
              <UiV2Route>
                <V2MyGameReport />
              </UiV2Route>
            </AuthGuard>
          }
        />
        <Route
          path="/my-game/notes"
          element={
            <AuthGuard requireAuth={true}>
              <UiV2Route>
                <V2MyGameNotesHistory />
              </UiV2Route>
            </AuthGuard>
          }
        />
        <Route
          path="/my-game/notes/:type/:noteId"
          element={
            <AuthGuard requireAuth={true}>
              <UiV2Route>
                <V2NoteThreadScreen />
              </UiV2Route>
            </AuthGuard>
          }
        />
        <Route
          path="/messages"
          element={
            <AuthGuard requireAuth={true}>
              <UiV2Route>
                <V2TopicsScreen />
              </UiV2Route>
            </AuthGuard>
          }
        />
        <Route
          path="/messages/new"
          element={
            <AuthGuard requireAuth={true}>
              <UiV2Route>
                <V2NewTopicScreen />
              </UiV2Route>
            </AuthGuard>
          }
        />
        <Route
          path="/messages/:id"
          element={
            <AuthGuard requireAuth={true}>
              <UiV2Route>
                <V2ThreadScreen />
              </UiV2Route>
            </AuthGuard>
          }
        />
        <Route
          path="/my-game/plan"
          element={
            <AuthGuard requireAuth={true}>
              <UiV2Route>
                <V2MyGamePlan />
              </UiV2Route>
            </AuthGuard>
          }
        />
        {/* Community — v2 only. There is no v1 counterpart, so these
            render through UiV2Route rather than UiRoute; a v1 session
            has no tab pointing at them. */}
        <Route
          path="/community"
          element={
            <AuthGuard requireAuth={true}>
              <MemberAccessGuard>
                <UiV2Route><V2Community /></UiV2Route>
              </MemberAccessGuard>
            </AuthGuard>
          }
        />
        <Route
          path="/community/settings"
          element={
            <AuthGuard requireAuth={true}>
              <MemberAccessGuard>
                <UiV2Route><V2CommunitySettings /></UiV2Route>
              </MemberAccessGuard>
            </AuthGuard>
          }
        />
        {/* Community's three header doors. */}
        <Route
          path="/community/buddies"
          element={
            <AuthGuard requireAuth={true}>
              <MemberAccessGuard>
                <UiV2Route><V2TeeBuddies /></UiV2Route>
              </MemberAccessGuard>
            </AuthGuard>
          }
        />
        <Route
          path="/community/scan"
          element={
            <AuthGuard requireAuth={true}>
              <MemberAccessGuard>
                <UiV2Route><V2CommunityScan /></UiV2Route>
              </MemberAccessGuard>
            </AuthGuard>
          }
        />
        {/* Not under /community: the inbox carries every push the member
            has been sent, not only Community's. */}
        <Route
          path="/notifications"
          element={
            <AuthGuard requireAuth={true}>
              <UiV2Route><V2Notifications /></UiV2Route>
            </AuthGuard>
          }
        />
        <Route
          path="/community/members/:memberId"
          element={
            <AuthGuard requireAuth={true}>
              <MemberAccessGuard>
                <UiV2Route><V2CommunityMember /></UiV2Route>
              </MemberAccessGuard>
            </AuthGuard>
          }
        />
        <Route
          path="/community/tee-time/new"
          element={
            <AuthGuard requireAuth={true}>
              <MemberAccessGuard>
                <UiV2Route><V2ShareTeeTime /></UiV2Route>
              </MemberAccessGuard>
            </AuthGuard>
          }
        />
        <Route
          path="/community/tee-time/:itemId"
          element={
            <AuthGuard requireAuth={true}>
              <MemberAccessGuard>
                <UiV2Route><V2TeeTimeDetail /></UiV2Route>
              </MemberAccessGuard>
            </AuthGuard>
          }
        />
        {/* A scanned QR or a shared link. Behind the guard like everything
            else: a member who is signed out is sent to login and returned
            here afterwards, so the code survives the detour. */}
        <Route
          path="/community/connect/:code"
          element={
            <AuthGuard requireAuth={true}>
              <MemberAccessGuard>
                <UiV2Route><V2CommunityConnect /></UiV2Route>
              </MemberAccessGuard>
            </AuthGuard>
          }
        />
        <Route
          path="/chat"
          element={
            <AuthGuard requireAuth={true}>
              <BaseLayout title="Chat" showTitle={true}>
                <Chat />
              </BaseLayout>
            </AuthGuard>
          }
        />
        <Route element={<BaseLayout />}>
          <Route
            path="/bookings/all"
            element={
              <AuthGuard requireAuth={true}>
                <UiRoute v1={<AllBookings />} v2={<V2AllBookings />} />
              </AuthGuard>
            }
          />
          {/* Session details — v2 only. v1 has no such screen, so a
              v1 session (or an old deep link) resolves to the list
              the booking came from rather than 404ing. */}
          <Route
            path="/bookings/:bookingId"
            element={
              <AuthGuard requireAuth={true}>
                <UiRoute
                  v1={<Navigate to="/bookings/all" replace />}
                  v2={<V2SessionDetails />}
                />
              </AuthGuard>
            }
          />
          <Route
            path="/shop"
            element={
              <AuthGuard requireAuth={true}>
                <MemberAccessGuard>
                  <UiRoute v1={<Shop />} v2={<V2Shop />} />
                </MemberAccessGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/shop-product/:productId"
            element={
              <AuthGuard requireAuth={true}>
                <MemberAccessGuard>
                  <UiRoute v1={<ProductDetail />} v2={<V2ProductDetail />} />
                </MemberAccessGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/shop-cart"
            element={
              <AuthGuard requireAuth={true}>
                <MemberAccessGuard>
                  <Suspense fallback={<div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>}>
                    <UiRoute v1={<Checkout />} v2={<V2Cart />} />
                  </Suspense>
                </MemberAccessGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/shop-order-success"
            element={
              <AuthGuard requireAuth={true}>
                <UiRoute v1={<OrderSuccess />} v2={<V2OrderSuccess />} />
              </AuthGuard>
            }
          />
          <Route
            path="/booking-type/location"
            element={
              <AuthGuard requireAuth={true}>
                <MemberAccessGuard>
                  <LocationPage />
                </MemberAccessGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/booking-type"
            element={
              <AuthGuard requireAuth={true}>
                <MemberAccessGuard>
                  <BookingLayout showFooter={false} showHeader={false}>
                    <UiRoute v1={<BookingType />} v2={<V2BookingType />} />
                  </BookingLayout>
                </MemberAccessGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/booking-slots"
            element={
              <AuthGuard requireAuth={true}>
                <MemberAccessGuard>
                  <UiRoute v1={<SelectSlots />} v2={<V2SelectSlots />} />
                </MemberAccessGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/booking-success"
            element={
              <AuthGuard requireAuth={true}>
                <UiRoute v1={<BookingSuccess />} v2={<V2BookingSuccess />} />
              </AuthGuard>
            }
          />
        </Route>
        {/* Test routes with BaseLayout */}
        <Route element={<BaseLayout />}>
          <Route path={PublicRoutePath.inputTest} element={<InputTest />} />
          <Route path={PublicRoutePath.buttonTest} element={<ButtonTest />} />
          <Route path={PublicRoutePath.footerNavTest} element={<FooterNavDemo />} />
        </Route>
        
        {/* Location Verification route - checks if user is within range */}
        <Route path="/access-control/location-verification" element={
          <BaseLayout>
            <MemberAccessGuard>
              <LocationVerification />
            </MemberAccessGuard>
          </BaseLayout>
        } />

        {/* OpenGate route with BaseLayout and back button */}
        <Route path="/access-control/open-gate" element={
          <BaseLayout>
            <MemberAccessGuard>
              <OpenGate />
            </MemberAccessGuard>
          </BaseLayout>
        } />
        
        {/* Waiver signing route — full screen, no layout, no nav bar */}
        <Route
          path="/waiver"
          element={
            <AuthGuard requireAuth={true}>
              <UiRoute v1={<WaiverSigningPage />} v2={<V2WaiverSigning />} />
            </AuthGuard>
          }
        />

        {/* Logout route */}
        <Route path="/logout" element={<LogoutHandler />} />

        {/* Public microsite routes — no AuthGuard */}
        <Route path="/p" element={<MicrositeLayout />}>
          <Route index element={<Navigate to="/p/access" replace />} />
          <Route path="access" element={<MicrositeAccessPage />} />
          <Route path="error" element={<MicrositeErrorPage />} />
          <Route path=":shareId" element={<MicrositeHomePage />} />
          <Route path=":shareId/report" element={<MicrositeReportPage />} />
          <Route path=":shareId/plan" element={<MicrositePlanPage />} />
          <Route path=":shareId/benefits" element={<MicrositeBenefitsPage />} />
        </Route>

        {/* Public OTP-gated share-report microsite — no AuthGuard, no
            member layout, always v2-styled (see PublicV2Route). The
            legacy /p/* microsite block above stays reachable but
            nothing routes to it from this new share flow (D-08). */}
        <Route
          path="/report/:shareId"
          element={
            <PublicV2Route>
              <V2ShareReport />
            </PublicV2Route>
          }
        />

        {/* Public survey routes — no AuthGuard */}
        <Route path="/survey" element={<SurveyLayout />}>
          <Route path="error" element={<SurveyErrorPage />} />
          <Route path=":token" element={<SurveyFormPage />} />
        </Route>

        {/* Public guest waiver routes — no AuthGuard */}
        <Route path="/guest-waiver" element={<GuestWaiverLayout />}>
          <Route path=":lId" element={<GuestWaiverPage />} />
        </Route>
      </Routes>
      </div>
    </CartProvider>
    </ThemeProvider>
  );

  /* Demo sessions stay out of product analytics. */
  return POSTHOG_KEY && !isDemoMode ? (
    <PostHogProvider apiKey={POSTHOG_KEY} options={POSTHOG_OPTIONS}>
      {content}
    </PostHogProvider>
  ) : (
    content
  );
}

export default function AppWithProviders() {
  return (
    <VersionSyncProvider>
      <ToastProvider>
        <NativeCalendarResultBridge />
        {/* Drops a secondary-club choice as soon as the member
            leaves the booking flow, by any route. */}
        <BookingLocationScope />
        {/* Tells the native shell which way to draw the status bar
            over the screen the member is on. No-op in a browser. */}
        <StatusBarScope />
        <UpdateRequiredModal />
        <ChatWidgetProvider>
          <App />
          <ChatWidget />
          {/* The desktop installer card. Outside `.app-root` on
              purpose — it pins to the window's bottom-left corner,
              in the surround beside the app column, so it never
              covers a screen. v1 gets nothing. */}
          <UiBlock v1={null} v2={<V2InstallPrompt />} />
        </ChatWidgetProvider>
      </ToastProvider>
    </VersionSyncProvider>
  );
}
